'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { analytics } from '@/lib/analytics';
import { RelatedTone } from '@/utils/helpers';
import { ToneSection } from '../ToneSection/ToneSection';
import { ToneRowTracking } from '../ToneSection/ToneRowTracking';

interface Props {
  toneId: number;
  limit?: number;
}

const DEFAULT_LIMIT = 4;

// Embeddings-backed recommendations, fetched client-side so the tone page's
// SSR path is unchanged. Renders nothing (not even its divider) until there is
// something to show, since coverage depends on the embedding backfill.
export const RelatedTones = ({ toneId, limit = DEFAULT_LIMIT }: Props) => {
  const [tones, setTones] = useState<RelatedTone[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    setTones([]);

    const load = async () => {
      try {
        const response = await fetch(`/api/tones/related?toneId=${toneId}&limit=${limit}`, {
          signal: controller.signal,
        });
        // Failures are captured too, with no `count`: silence would count them
        // as tones nobody asked about, and a 0 would count them as coverage
        // gaps. Neither is true.
        if (!response.ok) {
          analytics.capture('tone.related_tones.loaded', {
            tone_id: toneId,
            ok: false,
            status: response.status,
          });
          return;
        }

        const { tones: related = [], sourceModelId = null } = await response.json();
        setTones(related);
        // Captured even at count 0 — that's the coverage denominator.
        analytics.capture('tone.related_tones.loaded', {
          tone_id: toneId,
          ok: true,
          count: related.length,
          source_model_id: sourceModelId,
        });
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') return;
        console.error('Related tones fetch failed:', error);
        // No `status`: the request never got a response.
        analytics.capture('tone.related_tones.loaded', { tone_id: toneId, ok: false });
      }
    };

    load();
    return () => controller.abort();
  }, [toneId, limit]);

  // Stable identities: ToneRowTracking puts these straight into a context
  // value, so fresh literals would re-render every card in the row.
  const properties = useMemo(() => ({ source_tone_id: toneId }), [toneId]);
  const cardProperties = useCallback(
    (_toneId: number, position: number) => ({ distance: tones[position]?.distance }),
    [tones]
  );

  if (!tones.length) return null;

  return (
    <>
      <div className="flex border-t border-zinc-500 border-opacity-50" id="related-tones" />
      <ToneRowTracking
        surface="tone_page_related"
        tones={tones}
        properties={properties}
        cardProperties={cardProperties}
      >
        <ToneSection
          title="Related Tones"
          titleClassName="text-xl font-semibold"
          tones={tones}
          gridLimit={limit}
        />
      </ToneRowTracking>
    </>
  );
};
